import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { FluentProvider } from '@fluentui/react-components';
import { themeForMode } from './ptcThemes';
import {
  THEME_ATTRIBUTE,
  THEME_STORAGE_KEY,
  ThemeContext,
  readStoredMode,
  resolveInitialMode,
  useThemeMode,
} from './themeMode';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState(resolveInitialMode);

  const setMode = useCallback((next: 'light' | 'dark') => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // persistence is best-effort; the in-memory mode still applies
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  // Live-follow OS theme changes until the user makes an explicit choice.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    if (typeof mql.addEventListener !== 'function') return;
    const onChange = (event: MediaQueryListEvent) => {
      if (readStoredMode() !== null) return; // explicit override in place
      setModeState(event.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Key the active mode onto :root — index.css scopes every dark variable
  // override to `:root[data-tl-theme="dark"]`, and index.html's pre-mount FOUC
  // guard sets the same attribute before React loads so there is no flash.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute(THEME_ATTRIBUTE, mode);
    root.style.colorScheme = mode; // native scrollbars, form controls, etc.
  }, [mode]);

  const value = useMemo(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** FluentProvider bound to the active PTC theme (light/dark). */
export function ThemedFluentProvider({ children }: { children: ReactNode }) {
  const { mode } = useThemeMode();
  return <FluentProvider theme={themeForMode(mode)}>{children}</FluentProvider>;
}
